import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const navItems = [
  { id: 'flight-scoring', label: 'Flight Scoring', icon: 'ri-bar-chart-box-line' },
  { id: 'radio-transcription', label: 'Radio Transcription', icon: 'ri-radio-line' },
  { id: 'pattern-analysis', label: 'Pattern Analysis', icon: 'ri-flight-takeoff-line' },
  { id: 'coaching-insights', label: 'Coaching Insights', icon: 'ri-lightbulb-line' },
  { id: 'progress-tracking', label: 'Progress Tracking', icon: 'ri-line-chart-line' },
  { id: 'cockpit-support', label: 'Cockpit Support', icon: 'ri-brain-line' },
];

export default function FeaturesNav({ scrolled }: { scrolled: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState('flight-scoring');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isOnFeaturesPage = location.pathname === '/features';

  useEffect(() => {
    const handleScroll = () => {
      for (let i = navItems.length - 1; i >= 0; i--) {
        const el = document.getElementById(navItems[i].id);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActive(navItems[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    if (isOnFeaturesPage) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setMobileMenuOpen(false);
      }
    } else {
      navigate(`/features#${id}`);
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        } border-b border-[#00FFD1]/20`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 sm:gap-3 cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/2de32bf8-6894-4f4a-ba90-b9480aaa88cf.png"
              alt="WingmanFix Logo"
              className="h-8 w-8 sm:h-9 sm:w-9 object-contain"
            />
            <span className="text-base sm:text-lg font-bold tracking-tight whitespace-nowrap">WingmanFix</span>
          </button>

          {/* Desktop Feature anchors */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  active === item.id
                    ? 'bg-[#00FFD1]/10 text-[#00FFD1] border border-[#00FFD1]/30'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <i className={`${item.icon} text-sm`}></i>
                {item.label}
              </button>
            ))}
          </div>

          {/* Right side - Pricing + CTA + Hamburger */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/pricing')}
              className="hidden sm:block text-gray-400 hover:text-[#00FFD1] text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/waitlist')}
              className="bg-[#00A3E0] hover:bg-[#0088BB] text-white px-3 sm:px-5 py-2 rounded-full transition-all font-medium text-xs sm:text-sm whitespace-nowrap cursor-pointer"
            >
              Download App
            </button>
            
            {/* Hamburger menu button - mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-[#00FFD1]/30 bg-[#00FFD1]/10 text-[#00FFD1] cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile slide-down drawer */}
      <div
        className={`fixed top-[57px] left-0 right-0 z-40 bg-[#0A1628]/98 backdrop-blur-md border-b border-[#00FFD1]/20 transition-all duration-300 lg:hidden ${
          mobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 space-y-1 overflow-y-auto max-h-[calc(80vh-2rem)]">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                active === item.id
                  ? 'bg-[#00FFD1]/10 text-[#00FFD1] border border-[#00FFD1]/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <i className={`${item.icon} text-lg`}></i>
              {item.label}
            </button>
          ))}
          
          {/* Mobile Pricing link */}
          <button
            onClick={() => {
              navigate('/pricing');
              setMobileMenuOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-all cursor-pointer"
          >
            <i className="ri-price-tag-3-line text-lg"></i>
            Pricing
          </button>
        </div>
      </div>
    </>
  );
}